<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :title="title ? title : $i18n.t(isEdit ? 'dialog.editFeature' : 'dialog.addFeature')"
    :agree-text="agreeText ?? (isEdit ? 'dialog.agreeSave' : 'dialog.agreeAdd')"
    :loading="isDialogLoading"
    :is-valid="isFormValid && customIsValid"
    @agree="currentAction"
    v-bind="dialogData"
  >
    <template #body>
      <sidebar-row equal-padding>
        <slot name="prepend_form" />
      </sidebar-row>
      <v-form ref="dataForm" v-model="isFormValid" style="width: 100%">
        <feature-card-data :elements="formSchema" v-model="values" :is-editing-on="!disabledFeatureCard" equal-padding>
          <template v-for="(value, key) in $scopedSlots" #[key]="scope">
            <slot :name="key" v-bind="scope" />
          </template>
        </feature-card-data>
      </v-form>
    </template>
  </main-large-dialog>
</template>

<script>
import defaultValues from '@/mixins/defaultValues';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import { get, call } from 'vuex-pathify';
import { systemAttributes } from '@/assets/js/variables';

import FeatureCardData from '@/components/FeatureCardData';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'AddEditFeatureDialog',
  mixins: [defaultValues, prepareAttributesFeatureCardMixin],
  components: {
    FeatureCardData,
    SidebarRow,
    MainLargeDialog: () => import('@/components/MainLargeDialog'),
  },
  data: () => ({
    isDialogLoading: false,
    values: {},
    isFormValid: false,
    formSchema: [],
  }),
  props: {
    isVisible: {
      type: Boolean,
    },
    title: {
      type: String,
    },
    agreeText: {
      type: String,
    },
    featureData: {
      type: Object,
      default: () => ({
        id: null /** Optional - dialog in editing mode if not provided */,
        layer: null /** Optional */,
        datasource: null,
      }),
    },
    /** This should be optional. Expand this component if you need dynamic feature properties with id only */
    featureMetadata: {
      type: Object,
    },
    /**
     * Special keys:
     * - _systems - skips all system attributes
     */
    hiddenAttributes: {
      type: Array,
    },
    geometry: {
      type: Object,
    },
    staticProperties: {
      type: Object,
    },
    customDatasourceSchema: {
      type: Array,
      default: null,
    },
    dialogData: {
      type: Object,
      default: () => {
        return {};
      },
    },
    disabledFeatureCard: {
      type: Boolean,
      default: false,
    },
    customIsValid: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    computedIsVisible: {
      immediate: true,
      handler(nV) {
        if (!nV) return;
        this.prepareData(this.featureData);
      },
    },
  },
  computed: {
    layers: get('layers/layers'),
    layersMetadata: get('layers/metadata'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    isEdit() {
      return !!this.featureData.id;
    },
    isNew() {
      // Used in mixins
      return !this.isEdit;
    },
    currentAction() {
      return Object.keys(this.$listeners).includes('customAction') ? this.customAction : this.handleAddEdit;
    },
  },
  methods: {
    addNonTopoFeatureAction: call('layers/addDatasourceFeature'),
    editNonTopoFeatureAction: call('layers/editDatasourceFeature'),
    getHiddenAttributes() {
      return this.hiddenAttributes.reduce(
        (acc, item) => (acc = [...acc, ...(item === '_systems' ? systemAttributes : [item])]),
        []
      );
    },
    prepareData({ layer, datasource }) {
      this.$refs['dataForm']?.resetValidation();
      this.copyFeatureData = JSON.parse(JSON.stringify(this.featureData));
      const datasourceSchema = this.layersMetadata[datasource].attributes_schema;
      this.formSchema = this.getForm(
        layer ? this.layers[layer]?.form_schema.elements : null,
        this.customDatasourceSchema ?? datasourceSchema.attributes,
        {
          ...(Object.keys(this.staticProperties || {}).length && {
            disabledAttributes: Object.keys(this.staticProperties || {}),
          }),
          ...(this.hiddenAttributes && {
            filteredOutAttributes: datasourceSchema.attributes
              .filter(attr => this.getHiddenAttributes().includes(attr.name))
              .map(attr => attr.name),
          }),
        }
      );
      const geometryAttribute = this.geometry
        ? {
            [datasourceSchema.geometry_name]: this.geometry,
          }
        : {};
      let additionalValues = { ...(this.staticProperties ?? {}), ...geometryAttribute };
      if (!this.featureMetadata) return (this.values = { ...additionalValues });
      const idAttributeName = datasourceSchema.id_name;
      const properties = this.getFlattenProperties(this.featureMetadata, idAttributeName);
      this.values = {
        ...this.getValues(this.formSchema, properties),
        ...additionalValues,
      };
    },
    successCloseDialog() {
      this.computedIsVisible = this.isDialogLoading = false;
    },
    setDialogLoading(value) {
      this.isDialogLoading = value;
    },
    getBody() {
      return {
        ...this.values,
        ...(this.staticProperties ? this.staticProperties : {}),
      };
    },
    customAction() {
      this.$emit('customAction', this.getBody(), this.successCloseDialog, this.setDialogLoading, this.featureData);
    },
    async handleAddEdit() {
      this.setDialogLoading(true);
      try {
        let newData;
        if (this.isEdit) {
          await this.editNonTopoFeatureAction({
            datasource: this.featureData.datasource,
            id: this.featureData.id,
            body: this.getBody(),
            params: this.featureData.layer ? { layer_id: this.featureData.layer } : {},
          });
        } else {
          newData = await this.addNonTopoFeatureAction({
            datasource: this.featureData.datasource,
            body: this.getBody(),
            params: this.featureData.layer ? { layer_id: this.featureData.layer } : {},
          });
        }
        Object.keys(this.$listeners).includes('done')
          ? this.$emit('done', this.successCloseDialog, this.featureData, newData?.data?.data)
          : this.successCloseDialog();
      } catch (error) {
        console.error(error);
        this.setDialogLoading(false);
      }
    },
    attachEvents() {
      if ('values:change' in this.$listeners) {
        this.$watch(
          () => structuredClone(this.values),
          (values, oldValues) => this.$listeners['values:change'](values, oldValues),
          { deep: true }
        );
      }
    },
  },
  mounted() {
    this.attachEvents();
  },
};
</script>
